// The design's preset taxonomy (DESIGN_SPEC §5; design_logic.js SK, LN, MODES, MIRROR, buildLib):
// a preset is section type × layout × colour mode × mirror. Section types are data on the server
// (`section_types`, Admins add custom ones); these are the 12 built-in ones the design seeds.
// Names are not here: they are i18n strings (`library:sections.*`, `library:layouts.*`, `library:modes.*`).
import type { IconName } from '../icons';
import { TEMPLATES, type Layout } from './templates';

export type { Layout };

/** The 19 layouts, in the design's order (LN). */
export const LAYOUTS = Object.keys(TEMPLATES) as Layout[];

export function isLayout(v: unknown): v is Layout {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(TEMPLATES, v);
}

/** The 4 colour modes (MODES): light «Светлый», soft «Мягкий», inv «Тёмный», acc «Акцентный». */
export const COLOR_MODES = ['light', 'soft', 'inv', 'acc'] as const;
export type ColorMode = (typeof COLOR_MODES)[number];

export function isColorMode(v: unknown): v is ColorMode {
  return typeof v === 'string' && (COLOR_MODES as readonly string[]).includes(v);
}

/** The 11 layouts that have a mirrored variant (MIRROR): «· зеркально». */
export const MIRRORABLE: ReadonlySet<Layout> = new Set<Layout>([
  'hero-split', 'hero-full', 'about', 'contact', 'header', 'header-promo', 'banner', 'carousel', 'quote', 'mosaic', 'footer-min',
]);

export type SectionTypeDef = {
  key: string;
  icon: IconName;
  /** the layouts this type allows, in the design's order */
  layouts: Layout[];
  /** aspect ratios: the page preview stack and the gallery card (CSS aspect-ratio values) */
  previewRatio: string;
  galleryRatio: string;
};

/** The 12 built-in section types (SK). */
export const SECTION_TYPES: SectionTypeDef[] = [
  { key: 'header', icon: 'panel-top', layouts: ['header', 'header-center', 'header-promo'], previewRatio: '16 / 1.25', galleryRatio: '16 / 2.6' },
  { key: 'hero', icon: 'image', layouts: ['hero-split', 'hero-full', 'hero-text', 'about', 'banner'], previewRatio: '16 / 7', galleryRatio: '16 / 8' },
  { key: 'categories', icon: 'layout-grid', layouts: ['grid4', 'mosaic', 'carousel', 'cards'], previewRatio: '16 / 5.2', galleryRatio: '16 / 7' },
  { key: 'featured', icon: 'shopping-bag', layouts: ['cards', 'carousel', 'mosaic', 'grid4'], previewRatio: '16 / 6', galleryRatio: '16 / 7.5' },
  { key: 'promotions', icon: 'percent', layouts: ['banner', 'hero-full', 'hero-text'], previewRatio: '16 / 3.2', galleryRatio: '16 / 4.5' },
  { key: 'benefits', icon: 'badge-check', layouts: ['icons3', 'grid4', 'about'], previewRatio: '16 / 4.6', galleryRatio: '16 / 6.5' },
  { key: 'about', icon: 'info', layouts: ['about', 'quote', 'hero-split', 'icons3'], previewRatio: '16 / 5.6', galleryRatio: '16 / 7' },
  { key: 'reviews', icon: 'message-square-quote', layouts: ['quote', 'carousel', 'cards'], previewRatio: '16 / 4.4', galleryRatio: '16 / 6.5' },
  { key: 'faq', icon: 'circle-help', layouts: ['faq', 'about'], previewRatio: '16 / 5', galleryRatio: '16 / 7' },
  { key: 'contact', icon: 'map-pin', layouts: ['contact', 'about', 'hero-text'], previewRatio: '16 / 5.2', galleryRatio: '16 / 7' },
  { key: 'footer', icon: 'panel-bottom', layouts: ['footer', 'footer-min'], previewRatio: '16 / 3', galleryRatio: '16 / 4.2' },
  { key: 'custom', icon: 'square-dashed', layouts: ['custom'], previewRatio: '16 / 3', galleryRatio: '16 / 4.5' },
];

const BY_KEY = new Map(SECTION_TYPES.map((t) => [t.key, t]));
const CUSTOM = BY_KEY.get('custom') as SectionTypeDef;

/** A built-in type, or the custom block's geometry for an Admin's own type (design: SKk = SK[k] || SK.custom). */
export function sectionType(key: string): SectionTypeDef {
  return BY_KEY.get(key) ?? { ...CUSTOM, key, icon: 'shapes' };
}

export function isBuiltinSectionType(key: string): boolean {
  return BY_KEY.has(key);
}

/** One parametric preset of the design's demo library. */
export type DemoPreset = {
  /** `section:layout:mode:mirror`, e.g. `hero:hero-split:light:0` */
  id: string;
  section: string;
  layout: Layout;
  mode: ColorMode;
  mirrored: boolean;
  /** `v1`…`v5`, as the design numbers them */
  version: number;
  /** every 17th preset is a draft (only Admins see drafts) */
  status: 'published' | 'draft';
};

/**
 * The design's 233 parametric presets (buildLib): every section type × its layouts × 4 modes ×
 * mirror where the layout allows it; the custom block only in light. The API seeds the same set
 * with origin = demo (P3-D01).
 */
export function demoLibrary(): DemoPreset[] {
  const out: DemoPreset[] = [];
  let n = 0;
  for (const t of SECTION_TYPES)
    for (const layout of t.layouts)
      for (const mode of COLOR_MODES)
        for (const mi of [0, 1]) {
          if (mi && !MIRRORABLE.has(layout)) continue;
          if (t.key === 'custom' && mode !== 'light') continue;
          n++;
          out.push({ id: `${t.key}:${layout}:${mode}:${mi}`, section: t.key, layout, mode, mirrored: !!mi, version: 1 + ((n * 7) % 5), status: n % 17 === 0 ? 'draft' : 'published' });
        }
  return out;
}
